import React, { useState, useRef } from 'react';
import { X, Save, Trash2, Edit2, GripVertical } from 'lucide-react';

const CustomerTypeEditor = ({ customerType, onSave, onCancel, windowTypes, isDarkMode, onDelete }) => {
  // Initialize windowPricesOrder: use existing order if available, otherwise derive from windowPrices keys
  const initialOrder = customerType.windowPricesOrder || Object.keys(customerType.windowPrices || {});
  
  const [tempCustomerData, setTempCustomerData] = useState({
    ...customerType,
    windowPrices: { ...customerType.windowPrices },
    windowPricesOrder: [...initialOrder], // Explicit order array
    usesBusinessName: customerType?.usesBusinessName ?? false
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddWindowModal, setShowAddWindowModal] = useState(false);
  const [editingWindowTypeId, setEditingWindowTypeId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [displayWindowTypes, setDisplayWindowTypes] = useState(null);
  const isDraggingRef = useRef(false);
  const originalDraggedIndexRef = useRef(null);

  const updateWindowPrice = (windowTypeName, price) => {
    setTempCustomerData({
      ...tempCustomerData,
      windowPrices: {
        ...tempCustomerData.windowPrices,
        [windowTypeName]: price // Keep as string during editing
      }
    });
  };

  const removeWindowTypeFromPricing = (windowTypeName) => {
    const newWindowPrices = { ...tempCustomerData.windowPrices };
    delete newWindowPrices[windowTypeName];
    const newOrder = tempCustomerData.windowPricesOrder.filter(name => name !== windowTypeName);
    setTempCustomerData({
      ...tempCustomerData,
      windowPrices: newWindowPrices,
      windowPricesOrder: newOrder
    });
  };

  // Get window types that are already added to this customer type
  const addedWindowTypeNames = Object.keys(tempCustomerData.windowPrices);
  
  // Get window types that are available to add (not already in this customer type)
  const availableWindowTypes = windowTypes.filter(
    wt => !addedWindowTypeNames.includes(wt.name)
  );

  // Get window types that are currently in this customer type's pricing
  // Use windowPricesOrder if available, otherwise fall back to object keys order
  const orderToUse = tempCustomerData.windowPricesOrder && tempCustomerData.windowPricesOrder.length > 0
    ? tempCustomerData.windowPricesOrder.filter(name => addedWindowTypeNames.includes(name))
    : addedWindowTypeNames;
  
  const customerWindowTypes = orderToUse
    .map(name => windowTypes.find(wt => wt.name === name))
    .filter(wt => wt !== undefined);

  // Drag and drop handlers for window types
  const handleDragStart = (e, index) => {
    // Prevent drag if editing
    if (editingWindowTypeId !== null) {
      e.preventDefault();
      return;
    }
    
    // Only prevent drag if clicking directly on interactive elements
    const target = e.target;
    const isDirectInteractiveElement = target.tagName === 'INPUT' || 
                                      target.tagName === 'BUTTON' || 
                                      target.tagName === 'SELECT' ||
                                      target.tagName === 'TEXTAREA';
    
    const isInsideInteractiveElement = target.closest('input') || 
                                      target.closest('button') || 
                                      target.closest('select') ||
                                      target.closest('textarea');
    
    if (isDirectInteractiveElement || isInsideInteractiveElement) {
      e.preventDefault();
      return;
    }
    
    isDraggingRef.current = true;
    originalDraggedIndexRef.current = index;
    setDraggedItem(index);
    setDisplayWindowTypes([...customerWindowTypes]);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    
    if (originalDraggedIndexRef.current === null) return;
    if (draggedItem === index) return;
    
    setDisplayWindowTypes(currentDisplay => {
      const items = currentDisplay || customerWindowTypes;
      const newItems = [...items];
      const currentDraggedIndex = draggedItem !== null ? draggedItem : originalDraggedIndexRef.current;
      const draggedItemContent = newItems[currentDraggedIndex];
      newItems.splice(currentDraggedIndex, 1);
      newItems.splice(index, 0, draggedItemContent);
      return newItems;
    });
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    if (displayWindowTypes !== null) {
      // Reorder windowPrices and windowPricesOrder to match the new order
      const newWindowPrices = {};
      const newOrder = [];
      displayWindowTypes.forEach(windowType => {
        newWindowPrices[windowType.name] = tempCustomerData.windowPrices[windowType.name];
        newOrder.push(windowType.name);
      });
      
      setTempCustomerData({
        ...tempCustomerData,
        windowPrices: newWindowPrices,
        windowPricesOrder: newOrder
      });
      
      setDisplayWindowTypes(null);
    }
    
    setTimeout(() => {
      isDraggingRef.current = false;
      setDraggedItem(null);
      originalDraggedIndexRef.current = null;
    }, 100);
  };

  const addWindowTypeToCustomer = (windowType) => {
    const newOrder = [...(tempCustomerData.windowPricesOrder || []), windowType.name];
    setTempCustomerData({
      ...tempCustomerData,
      windowPrices: {
        ...tempCustomerData.windowPrices,
        [windowType.name]: windowType.price // Use default price from window type
      },
      windowPricesOrder: newOrder
    });
    setShowAddWindowModal(false);
  };

  return (
    <div className="min-h-screen bg-primary">
      <div className="customer-type-editor-header sticky top-0 z-10">
        <button onClick={onCancel} className="customer-type-cancel-btn">
          <X className="w-5 h-5" />
        </button>
        <div className="customer-type-header-title">
          <div className="customer-type-header-line1">Editing</div>
          <div className="customer-type-header-line2">{tempCustomerData.name} Pricing</div>
        </div>
        <div className="customer-type-header-actions">
          <button
            onClick={() => setShowDeleteModal(true)}
            className="edit-btn-wide bg-danger text-white window-type-delete-btn"
            title="Delete Customer Type"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button 
            onClick={() => onSave(tempCustomerData)} 
            className="edit-btn-wide btn-primary window-type-save-btn"
            title="Save Changes"
          >
            <Save className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        <div className="card">
          <h2 className="text-xl font-bold text-primary mb-4">Customer Type Name</h2>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <input
              type="text"
              value={tempCustomerData.name}
              onChange={(e) => setTempCustomerData({...tempCustomerData, name: e.target.value})}
              className="input-field focusable max-w-xs"
            />
            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-primary whitespace-nowrap">
                Name Style:
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTempCustomerData({...tempCustomerData, usesBusinessName: false})}
                  className={`px-4 py-2 rounded-lg font-medium transition-all border-2 ${
                    !tempCustomerData.usesBusinessName
                      ? 'bg-blue-600 text-white shadow-md border-transparent'
                      : 'bg-transparent text-primary border-primary'
                  }`}
                >
                  First & Last Name
                </button>
                <button
                  type="button"
                  onClick={() => setTempCustomerData({...tempCustomerData, usesBusinessName: true})}
                  className={`px-4 py-2 rounded-lg font-medium transition-all border-2 ${
                    tempCustomerData.usesBusinessName
                      ? 'bg-blue-600 text-white shadow-md border-transparent'
                      : 'bg-transparent text-primary border-primary'
                  }`}
                >
                  Business Name
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-bold text-primary mb-6">Window Type Pricing</h2>
          <div className="space-y-4">
            {customerWindowTypes.length === 0 ? (
              <div className="text-center py-8 text-tertiary">
                <p className="text-lg mb-2">No window types added yet</p>
                <p className="text-sm">Click "Add Window Type" to get started</p>
              </div>
            ) : (
              (displayWindowTypes !== null ? displayWindowTypes : customerWindowTypes).map((windowType, index) => {
                const isDragged = draggedItem === index;
                
                return (
                <div 
                  key={windowType.id} 
                  draggable={editingWindowTypeId !== windowType.id}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center justify-between p-4 bg-quaternary rounded-xl border ${
                    editingWindowTypeId === windowType.id 
                      ? 'border-accent-primary border-2' 
                      : 'border-primary'
                  }`}
                  style={{
                    cursor: editingWindowTypeId === windowType.id ? 'default' : (isDragged ? 'grabbing' : 'grab'),
                    opacity: isDragged ? 0.5 : 1,
                    userSelect: 'none',
                    WebkitUserSelect: 'none'
                  }}
                >
                  <GripVertical 
                    className="w-5 h-5 text-tertiary flex-shrink-0 drag-handle mr-2" 
                    style={{ opacity: 0.5, cursor: 'grab', pointerEvents: 'auto' }}
                  />
                  <div className="flex-1">
                    <div className="font-bold text-lg text-primary">{windowType.name}</div>
                    <div className="text-sm text-tertiary font-medium">
                      {windowType.category === 'exterior_interior' ? 'Exterior/Interior' : 'Separate Item'}
                      <span className="ml-2 text-xs">
                        (Default: ${windowType.price})
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-tertiary mr-2 text-xl font-bold">$</span>
                    <input
                      type="text"
                      value={tempCustomerData.windowPrices[windowType.name] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          updateWindowPrice(windowType.name, value);
                        }
                      }}
                      onFocus={(e) => {
                        const input = e.target;
                        const length = input.value.length;
                        input.setSelectionRange(length, length);
                      }}
                      onClick={(e) => {
                        const input = e.target;
                        const length = input.value.length;
                        input.setSelectionRange(length, length);
                      }}
                      className="input-field text-right font-bold focusable"
                      style={{ width: '60px' }}
                    />
                    {editingWindowTypeId === windowType.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeWindowTypeFromPricing(windowType.name);
                            setEditingWindowTypeId(null);
                          }}
                          className="btn btn-icon bg-danger text-white"
                          title="Remove window type from this customer type"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingWindowTypeId(null);
                          }}
                          className="btn btn-icon"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                      </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingWindowTypeId(windowType.id);
                        }}
                        className="btn btn-icon"
                        title="Edit window type"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                );
              })
            )}
            
            {/* Add Window Type Button */}
            <button
              onClick={() => setShowAddWindowModal(true)}
              className="add-window-btn w-full"
              disabled={availableWindowTypes.length === 0}
            >
              <div className="flex items-center justify-center">
                <div className="text-4xl text-tertiary mb-2">+</div>
              </div>
              <div className="text-lg font-semibold text-tertiary">Add Window Type</div>
              <div className="text-sm text-tertiary">
                {availableWindowTypes.length === 0 
                  ? 'All window types added' 
                  : `Tap to select from ${availableWindowTypes.length} available window type${availableWindowTypes.length !== 1 ? 's' : ''}`
                }
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-secondary border border-primary rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-xl font-bold text-primary mb-4">Delete Customer Type</h3>
            <p className="text-tertiary mb-6">
              Are you sure you want to delete "{tempCustomerData.name}" customer type? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-danger flex-1"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onDelete(customerType.id);
                  setShowDeleteModal(false);
                }}
                className="btn bg-danger flex-1"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Window Type Modal */}
      {showAddWindowModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddWindowModal(false)}>
          <div className="bg-secondary border border-primary rounded-xl p-6 max-w-md mx-4 w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-primary">Select Window Type</h3>
              <button
                onClick={() => setShowAddWindowModal(false)}
                className="btn btn-icon"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-tertiary mb-4">
              Choose a window type to add to this customer type. The default price will be used initially.
            </p>
            {availableWindowTypes.length === 0 ? (
              <div className="text-center py-8 text-tertiary">
                <p>All window types have been added to this customer type.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {availableWindowTypes.map(windowType => (
                  <button
                    key={windowType.id}
                    onClick={() => addWindowTypeToCustomer(windowType)}
                    className="w-full text-left p-4 bg-quaternary rounded-xl border border-primary hover:bg-tertiary transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="font-bold text-lg text-primary">{windowType.name}</div>
                        <div className="text-sm text-tertiary">
                          {windowType.category === 'exterior_interior' ? 'Exterior/Interior' : 'Separate Item'}
                        </div>
                      </div>
                      <div className="text-xl font-bold text-primary">${windowType.price}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTypeEditor;
